import { Button, Col, Divider, Drawer, Form, Input, InputNumber, Row, Select, Space, message } from "antd"
import { useState, useEffect } from "react"
import { collection, doc, getDocs, query, updateDoc, where } from "firebase/firestore"
import { db } from "../../firebase/config"
import { useCollection } from "../../hooks/useCollection"

export default function EditClientFullDrawer({ record, open, onClose }) {
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)
  const { data: manufacturers } = useCollection("manufacturers")

  useEffect(() => {
    if (open && record) {
      form.setFieldsValue({
        client: record.client,
        vat: record.vat,
        reg: record.reg,
        country: record.country,
        city: record.city,
        street: record.street,
        postalCode: record.postalCode,
        manufacturerId: record.manufacturer?.id || null,
        bankName: record.bankName,
        bankCode: record.bankCode,
        accountNumber: record.accountNumber,
        creditLimit: record.creditLimit,
        deliveryAddress: record.deliveryAddress,
        deliveryAddress2: record.deliveryAddress2,
        contactName: record.contactName,
        contactEmail: record.contactEmail,
        contactNumber: record.contactNumber,
        accountant: record.accountant,
        accountantEmail: record.accountantEmail,
        accountantNumber: record.accountantNumber,
      })
    }
  }, [open, record, form])

  const onFinish = async (values) => {
    setSubmitting(true)
    try {
      const selectedManufacturer = manufacturers?.find((m) => m.id === values.manufacturerId) || null

      const clientUpdate = {
        client: values.client,
        vat: values.vat,
        reg: values.reg,
        country: values.country,
        city: values.city,
        street: values.street,
        postalCode: values.postalCode,
        bankName: values.bankName,
        bankCode: values.bankCode,
        accountNumber: values.accountNumber,
        creditLimit: values.creditLimit,
        deliveryAddress: values.deliveryAddress,
        deliveryAddress2: values.deliveryAddress2,
        contactName: values.contactName,
        contactEmail: values.contactEmail,
        contactNumber: values.contactNumber,
        accountant: values.accountant,
        accountantEmail: values.accountantEmail,
        accountantNumber: values.accountantNumber,
        ...(selectedManufacturer && { manufacturer: selectedManufacturer }),
      }

      await updateDoc(doc(db, "clients", record.id), clientUpdate)

      // Propagate client name to priceCalculation
      const pcSnap = await getDocs(
        query(collection(db, "priceCalculation"), where("clientId", "==", record.id))
      )
      await Promise.all(
        pcSnap.docs.map((d) => updateDoc(doc(db, "priceCalculation", d.id), { client: values.client }))
      )

      // Propagate to orders
      const ordersSnap = await getDocs(
        query(collection(db, "orders"), where("clientId", "==", record.id))
      )
      const orderUpdate = {
        "client.client": values.client,
        "client.vat": values.vat,
        "client.reg": values.reg,
        "client.street": values.street,
        "client.postalCode": values.postalCode,
        "client.city": values.city,
        "client.country": values.country,
        "client.deliveryAddress": values.deliveryAddress,
        "client.deliveryAddress2": values.deliveryAddress2,
        "client.contactName": values.contactName,
        "client.contactEmail": values.contactEmail,
        "client.contactNumber": values.contactNumber,
        "client.bankName": values.bankName,
        "client.bankCode": values.bankCode,
        "client.accountNumber": values.accountNumber,
        "client.accountant": values.accountant,
        "client.accountantEmail": values.accountantEmail,
        "client.accountantNumber": values.accountantNumber,
        ...(selectedManufacturer && {
          "client.manufacturer": selectedManufacturer,
          manufacturer: selectedManufacturer,
        }),
      }
      await Promise.all(ordersSnap.docs.map((d) => updateDoc(doc(db, "orders", d.id), orderUpdate)))

      message.success("Client updated successfully!")
      onClose()
    } catch (err) {
      console.error(err)
      message.error("Failed to update client")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Drawer
      title={`Edit – ${record?.client || ""}`}
      width={680}
      placement="right"
      open={open}
      onClose={onClose}
      destroyOnClose
      extra={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={submitting} onClick={() => form.submit()}>
            Save Changes
          </Button>
        </Space>
      }>
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Divider orientation="left" orientationMargin={0}>Company Information</Divider>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Company Name" name="client" rules={[{ required: true, message: "Required" }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="VAT" name="vat">
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Reg No" name="reg">
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Country" name="country">
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="City" name="city">
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Postal Code" name="postalCode">
              <Input />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="Street" name="street">
              <Input />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="Manufacturer" name="manufacturerId">
              <Select
                showSearch
                allowClear
                optionFilterProp="label"
                placeholder="Select manufacturer"
                options={manufacturers?.map((m) => ({ value: m.id, label: m.company }))}
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left" orientationMargin={0}>Financial & Bank</Divider>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Bank Name" name="bankName">
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Bank Code" name="bankCode">
              <Input />
            </Form.Item>
          </Col>
          <Col span={16}>
            <Form.Item label="Account No" name="accountNumber">
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Credit Limit (€)" name="creditLimit">
              <InputNumber style={{ width: "100%" }} min={0} />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left" orientationMargin={0}>Delivery Addresses</Divider>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item label="Primary" name="deliveryAddress">
              <Input />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="Secondary" name="deliveryAddress2">
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left" orientationMargin={0}>Contacts</Divider>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Contact Name" name="contactName">
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Contact Email" name="contactEmail">
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Contact Number" name="contactNumber">
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Accountant" name="accountant">
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Acct Email" name="accountantEmail">
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Acct Phone" name="accountantNumber">
              <Input />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Drawer>
  )
}
